import { defineStore } from 'pinia';
import { SecureStorage } from '@/utils/storage';

const BaseURL = process.env.VUE_APP_API_BASE_URL;
const CACHE_KEY = 'app_structure';

export const useStructureStore = defineStore('structure', {
  state: () => ({
    currentStructureId: null,
    currentStructureName: null,
    allStructures: {},
    structureImageUrl: '',
    dashboardName: '',
    has_regionals: false,
    structureContact: {},
    structureBoss: {},
    structureBossContact: {},
    structureRegional: [],
    StatePersonelsData: [],
    structureMissions: [],
    _cacheLoaded: false,
  }),
  actions: {
    _ensureCacheLoaded() {
      if (this._cacheLoaded) return;
      this._cacheLoaded = true;
      const cached = SecureStorage.getItem(CACHE_KEY);
      if (cached && cached.currentStructureId) {
        this.currentStructureId = cached.currentStructureId;
        this.currentStructureName = cached.currentStructureName;
        this.allStructures = cached.allStructures || {};
        this.structureImageUrl = cached.structureImageUrl || '';
        this.dashboardName = cached.dashboardName || '';
        this.has_regionals = cached.has_regionals || false;
        this.structureContact = cached.structureContact || {};
        this.structureBoss = cached.structureBoss || {};
        this.structureBossContact = cached.structureBossContact || {};
        this.structureRegional = cached.structureRegional || [];
        this.StatePersonelsData = cached.StatePersonelsData || [];
        this.structureMissions = cached.structureMissions || [];
      }
    },

    _loadFromCache() {
      const cached = SecureStorage.getItem(CACHE_KEY);
      if (cached && cached.currentStructureId) {
        Object.assign(this, cached);
        return true;
      }
      return false;
    },

    _saveToCache() {
      SecureStorage.setItem(CACHE_KEY, {
        currentStructureId: this.currentStructureId,
        currentStructureName: this.currentStructureName,
        allStructures: this.allStructures,
        structureImageUrl: this.structureImageUrl,
        dashboardName: this.dashboardName,
        has_regionals: this.has_regionals,
        structureContact: this.structureContact,
        structureBoss: this.structureBoss,
        structureBossContact: this.structureBossContact,
        structureRegional: this.structureRegional,
        StatePersonelsData: this.StatePersonelsData,
        structureMissions: this.structureMissions,
      }, SecureStorage.TTL.STRUCTURE);
    },

    setCurrentStructure(structure) {
      this._ensureCacheLoaded();
      if (!structure) {
        this.clearCurrentStructure();
        return;
      }
      this.currentStructureId = structure.id;
      this.currentStructureName = structure.name;
      this.allStructures = structure;
      this.dashboardName = structure.abbreviation || '';
      this.structureImageUrl = this.getFullImageUrl(structure.photo) || '';
      this._saveToCache();
    },

    resetLanguageDependentData() {
      const currentId = this.currentStructureId;
      this.$reset();
      this.currentStructureId = currentId;
    },

    clearCurrentStructure() {
      this.currentStructureId = null;
      this.currentStructureName = null;
      this.allStructures = {};
      this.structureImageUrl = '';
      this.dashboardName = '';
      this.has_regionals = false;
      this.structureContact = {};
      this.structureBoss = {};
      this.structureBossContact = {};
      this.structureRegional = [];
      this.StatePersonelsData = [];
      this.structureMissions = [];
      SecureStorage.removeItem(CACHE_KEY);
    },

    getFullImageUrl(path) {
      if (!path || typeof path !== 'string') return null;
      if (path.startsWith('http')) return path;
      return `${BaseURL}${path.startsWith('/') ? '' : '/'}${path}`;
    },

    setStructureData(data) {
      this.allStructures = { ...this.allStructures, ...data };
      this.structureImageUrl = this.getFullImageUrl(data.photo);
      this.dashboardName = data.abbreviation;
      this.has_regionals = data.has_regionals || false;
      this._saveToCache();
    },

    setStructureContact(data) {
      const filteredData = {};
      const contactFields = ['website_url', 'facebook_url', 'twitter_url', 'linkedin_url', 'instagram_url', 'google_map_directions', 'phone', 'email', 'location', 'tiktok_url', 'youtube_url'];
      const source = (data && typeof data[0] === 'object') ? data[0] : data;
      if (source) {
        contactFields.forEach(field => {
          if (source[field] != null) {
            filteredData[field] = source[field];
          }
        });
      }
      this.structureContact = filteredData;
      this._saveToCache();
    },

    setStructureBoss(data) {
      if (!data) {
        this.structureBoss = {};
        return;
      }
      this.structureBoss = {
        ...data,
        photo: this.getFullImageUrl(data.photo) || '/assets/images/avatar.png'
      };
      this._saveToCache();
    },

    setStructureBossContact(data) {
      this.structureBossContact = data || {};
      this._saveToCache();
    },

    setStructureRegionals(data) {
      this.structureRegional = (data || []).map(item => ({
        ...item,
        photo: this.getFullImageUrl(item.photo) || '/assets/images/default-regional.png'
      }));
      this._saveToCache();
    },

    setStatePersonnels(data) {
      this.StatePersonelsData = (data || []).map(item => ({
        ...item,
        photo: this.getFullImageUrl(item.photo) || '/assets/images/avatar.png'
      }));
      this._saveToCache();
    },

    setStructureMissions(data) {
      this.structureMissions = Array.isArray(data) ? data : (data ? [data] : []);
      this._saveToCache();
    },
  },
});
